import React, {
  ChangeEvent,
  InputHTMLAttributes,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import _get from 'lodash/get';
import { useFormikContext } from 'formik';

import { Theme } from '@app/theme';
import { useTrackChange, useTrackClick } from '@app/analytics';
import { IconButtonGroup } from '@app/core';

import { GenericInput } from './GenericInput';
import { clearError, useOptionalField } from './FormikFields';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    padding: 0,
  },
  input: {
    padding: `0 ${theme.marginBase * 2}px`,
    textAlign: 'center',
    minWidth: 60,
    width: '100%',
    '-moz-appearance': 'textfield',
    '&::placeholder': {
      color: theme.colors.inkTertiary,
    },
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    borderRightWidth: 0,
    borderRadius: 0,
    '&:last-child': {
      borderRightWidth: 1,
    },
  },
}));

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  containerClass?: string;
  rightAction?: React.ReactNode;
  value?: any;
  name?: string;
  error?: string;
  noMargin?: boolean;
  noError?: boolean;
  disabled?: boolean;
  onValueChange?: (value: any) => void;
  secondaryLabel?: string;
  onSubmit?: () => void;
  small?: boolean;
  trackingName?: string;
}

const NumberInputComponent = ({
  min,
  max,
  step,
  onValueChange,
  onSubmit,
  trackingName,
  ...props
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ ...props, theme });
  const { onChange, name, value } = props;

  const formik = useFormikContext<any>();
  const { field, helpers } = useOptionalField(props);
  const setValue = helpers && helpers.setValue;
  const setTouched = helpers && helpers.setTouched;
  const inputRef = useRef<HTMLInputElement>(null);

  const actualMin = useMemo(() => {
    if (min !== undefined && typeof min === 'number') {
      return min;
    }
    if (
      min !== undefined &&
      typeof min === 'string' &&
      formik &&
      formik.values
    ) {
      return _get(formik.values, min);
    }
    return;
  }, [formik, min]);

  const formikStatus = formik && formik.status;
  const addToValue = useCallback(
    (sign: number) => {
      const add = step
        ? typeof step === 'string'
          ? sign * parseInt(step)
          : sign * step
        : sign;

      const val: number =
        typeof value !== 'string'
          ? typeof value === 'number'
            ? value
            : 0
          : parseInt(value);

      let finalValue = val + add;
      if (actualMin !== undefined && finalValue < actualMin) {
        finalValue = actualMin;
      }
      if (max !== undefined && typeof max === 'number') {
        finalValue = finalValue > max ? max : finalValue;
      }
      if (inputRef.current) {
        // @ts-ignore
        var setValue = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        ).set;
        // @ts-ignore
        setValue.call(inputRef.current, finalValue);
        const event = new Event('change', { bubbles: true });
        inputRef.current.dispatchEvent(event);
      }
    },
    [name, max, onValueChange, actualMin, step, value, inputRef],
  );

  const onInputChange = useTrackChange(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (onValueChange) {
        onValueChange(e.currentTarget.value);
      }
      if (!formik || !name) {
        return onChange && onChange(e);
      }
      if (setValue && setTouched) {
        setTouched(true);
        setValue(Number(e.currentTarget.value));
      }
      if (formikStatus && name) {
        clearError(formikStatus, name);
      }
    },
    [formik, name, onChange, setValue, setTouched, formikStatus, onValueChange],
  );

  const increment = useTrackClick(() => addToValue(1), [addToValue]);
  const decrement = useTrackClick(() => addToValue(-1), [addToValue]);

  return (
    <div className={classes.container}>
      <IconButtonGroup
        onClick={decrement}
        type="button"
        disabled={props.disabled || (min !== undefined && field.value <= min)}
        id={`${props.name}_decrement`}
        color="green"
        icon="ri-subtract-line"
        title="Decrement"
        small={props.small}
        data-tracking-name={['Decrement', trackingName]
          .filter(Boolean)
          .join(' ')}
      />
      <input
        {...props}
        className={classnames(props.className, classes.input)}
        onChange={onInputChange}
        onKeyDown={(e) => {
          if (onSubmit && e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          }
        }}
        min={min}
        max={max}
        value={field.value?.toString() || ''}
        ref={inputRef}
        data-tracking-name={trackingName}
      />
      <IconButtonGroup
        onClick={increment}
        type="button"
        disabled={props.disabled || (max !== undefined && field.value >= max)}
        id={`${props.name}_increment`}
        color="green"
        icon="ri-add-line"
        title="Increment"
        small={props.small}
        data-tracking-name={['Increment', trackingName]
          .filter(Boolean)
          .join(' ')}
      />
    </div>
  );
};

export const NumberInput = (props: Props) => {
  return (
    <GenericInput component={NumberInputComponent} {...props} type="number" />
  );
};
